/*jshint esversion: 11 */
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Badge, Spinner, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { toast } from 'react-toastify';
import moment from 'moment';
import { getActiveSessions, terminateSession } from '../../api/sessions';

const ActiveSessionsTab = ({ currentUserId }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [terminating, setTerminating] = useState(null);
  const [confirmSession, setConfirmSession] = useState(null);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getActiveSessions();
      setSessions(res.data || []);
    } catch {
      toast.error('Failed to load active sessions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const handleTerminate = async () => {
    const sessionId = confirmSession._id;
    setConfirmSession(null);
    setTerminating(sessionId);
    try {
      await terminateSession(sessionId);
      toast.success('Session terminated');
      fetchSessions();
    } catch (e) {
      toast.error(e?.errors?.[0]?.msg || e?.message || 'Failed to terminate session');
    } finally {
      setTerminating(null);
    }
  };

  const expiresLabel = (expiresAt) => {
    const diff = moment(expiresAt).diff(moment(), 'hours');
    const label = moment(expiresAt).format('DD MMM YYYY, hh:mm A');
    return diff < 2
      ? <span className="text-danger">{label}</span>
      : <span>{label}</span>;
  };

  if (loading) return <div className="text-center py-4"><Spinner color="primary" /></div>;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span className="text-muted">{sessions.length} active session{sessions.length !== 1 ? 's' : ''}</span>
        <Button size="sm" color="light" onClick={fetchSessions}>Refresh</Button>
      </div>
      {sessions.length === 0 ? (
        <p className="text-muted text-center py-4">No active sessions found.</p>
      ) : (
        <div className="table-responsive">
          <Table className="table-centered table-nowrap mb-0">
            <thead className="thead-light">
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Logged in at</th>
                <th>Expires at</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => {
                const isOwn = s.user_id?.toString() === currentUserId?.toString()
                  && s.user_type === 'super_admin';
                return (
                  <tr key={s._id}>
                    <td>{s.name}</td>
                    <td>{s.phone_number}</td>
                    <td>
                      <Badge color={s.user_type === 'super_admin' ? 'danger' : 'primary'} pill>
                        {s.user_type === 'super_admin' ? 'Super Admin' : 'Staff'}
                      </Badge>
                    </td>
                    <td>{moment(s.createdAt).format('DD MMM YYYY, hh:mm A')}</td>
                    <td>{expiresLabel(s.expires_at)}</td>
                    <td>
                      {isOwn ? (
                        <Badge color="secondary" pill>Current</Badge>
                      ) : (
                        <Button
                          size="sm"
                          color="danger"
                          outline
                          disabled={terminating === s._id}
                          onClick={() => setConfirmSession(s)}
                        >
                          {terminating === s._id ? <Spinner size="sm" /> : 'Terminate'}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      <Modal isOpen={!!confirmSession} toggle={() => setConfirmSession(null)} centered>
        <ModalHeader toggle={() => setConfirmSession(null)}>Terminate Session</ModalHeader>
        <ModalBody>
          Are you sure you want to terminate <strong>{confirmSession?.name}</strong>'s session?
          They will be logged out immediately.
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setConfirmSession(null)}>Cancel</Button>
          <Button color="danger" onClick={handleTerminate}>Terminate</Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default ActiveSessionsTab;
